import React, { useRef, useState } from 'react';
import cx from 'classnames';
import { MdPublic } from 'react-icons/md';
import { deliveryDestinationsCodes } from '../../../common/deliveryDestinations';
import {
  DEFAULT_TRANSLATION_LANG,
  getTranslatedValue,
  Translations,
} from '../../../common/getTranslatedValue';
import {
  getLanguageTranslation,
  getTranslationLanguageDisplay,
  TranslationFieldProps,
  TranslationInput,
  TranslationText,
  TranslatedValueModal,
  type TranslationEdit,
} from './TranslatedValueModal';

export { getLanguageTranslation, TranslationText } from './TranslatedValueModal';
export type { TranslationEdit } from './TranslatedValueModal';

export { DEFAULT_TRANSLATION_LANG, getTranslatedValue };
export type { Translations };

export function getTranslation(
  translations: Translations | string | null | undefined,
  lang = DEFAULT_TRANSLATION_LANG,
): string {
  if (translations == null) return '';
  if (typeof translations === 'string') return translations;
  return translations[lang] ?? '';
}

export function mergeTranslation(
  translations: Translations | string | null | undefined,
  lang: string,
  text: string,
): Translations {
  return mergeTranslations(translations, [{ lang, newValue: text }]);
}

export function mergeTranslations(
  translations: Translations | string | null | undefined,
  updates: readonly TranslationEdit[],
): Translations {
  const base = typeof translations === 'string'
    ? (translations ? { [DEFAULT_TRANSLATION_LANG]: translations } : {})
    : { ...(translations ?? {}) };
  return updates.reduce((current, update) => ({
    ...current,
    [update.lang]: update.newValue,
  }), base);
}

function sameTranslations(
  left: Translations | string | null | undefined,
  right: Translations | string | null | undefined,
): boolean {
  if (left === right) return true;
  if (typeof left !== 'object' || typeof right !== 'object' || left == null || right == null) {
    return false;
  }

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
}

export function useTranslationEdits(source: Translations | string | null | undefined) {
  const latestRef = useRef(source);
  const acceptedRef = useRef(source);
  const [, setVersion] = useState(0);

  if (sameTranslations(latestRef.current, source) || sameTranslations(latestRef.current, acceptedRef.current)) {
    latestRef.current = source;
    acceptedRef.current = source;
  }

  const edit = (updates: TranslationEdit[]) => {
    const next = mergeTranslations(latestRef.current, updates);
    latestRef.current = next;
    setVersion((version) => version + 1);
    return next;
  };

  return {
    value: latestRef.current,
    edit,
  };
}

export function useTranslationEditsMap() {
  const latestRef = useRef(new Map<string, Translations | string | null | undefined>());
  const acceptedRef = useRef(new Map<string, Translations | string | null | undefined>());
  const [, setVersion] = useState(0);

  const value = (key: string, source: Translations | string | null | undefined) => {
    const hasLatest = latestRef.current.has(key);
    const latest = hasLatest ? latestRef.current.get(key) : undefined;
    const hasAccepted = acceptedRef.current.has(key);
    const accepted = hasAccepted ? acceptedRef.current.get(key) : undefined;

    if (!hasLatest || sameTranslations(latest, source) || sameTranslations(latest, hasAccepted ? accepted : source)) {
      latestRef.current.set(key, source);
      acceptedRef.current.set(key, source);
      return source;
    }

    return latest ?? source;
  };

  const edit = (
    key: string,
    source: Translations | string | null | undefined,
    updates: TranslationEdit[],
  ) => {
    const next = mergeTranslations(value(key, source), updates);
    latestRef.current.set(key, next);
    setVersion((version) => version + 1);
    return next;
  };

  return { value, edit };
}

export interface TranslatedValueProps extends TranslationFieldProps {
  translations: Translations | string | null | undefined;
  renderAs: 'text' | 'input';
  onEditTranslation: (updates: TranslationEdit[]) => void;
  languages?: readonly string[];
  defaultLang?: string;
  optimizeTitle?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const TranslatedValue: React.FC<TranslatedValueProps> = ({
  translations,
  renderAs,
  onEditTranslation,
  languages = deliveryDestinationsCodes,
  defaultLang = DEFAULT_TRANSLATION_LANG,
  optimizeTitle,
  className,
  style,
  label,
  placeholder,
  required,
  maxLength,
  multiline,
  rows,
  autoFocus,
  size,
  onKeyDown,
  disabled,
}) => {
  const [lang, setLang] = useState(defaultLang);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const language = getTranslationLanguageDisplay(lang);
  const filledCount = languages.filter((code) => getLanguageTranslation(translations, code)).length;
  const unfilledCount = languages.length - filledCount;

  return (
    <div className="group/lang relative min-w-0">
      <button
        type="button"
        className={cx(
          'absolute top-0 right-0 z-10 px-1.5 py-0.5 rounded-md border-gray-300 bg-white border flex items-center gap-1 whitespace-nowrap leading-none cursor-pointer',
          'text-sky-400 opacity-70 transition-all duration-150',
          'group-hover/lang:opacity-100 group-focus-within/lang:opacity-100',
        )}
        aria-label={`Langue ${language.name}, ${filledCount} renseignées, ${unfilledCount} manquantes`}
        title={`${filledCount} renseignées / ${unfilledCount} manquantes`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsModalOpen(true);
        }}
      >
        <MdPublic className="w-3.5 h-3.5 shrink-0" />
        <span className="text-xs font-semibold">{language.code}</span>
        <span className="hidden text-xs font-medium text-gray-600 group-hover/lang:inline group-focus-within/lang:inline">
          {filledCount}/{unfilledCount}
        </span>
      </button>
      <div className="min-w-0 break-words">
        {renderAs === 'text' ? (
          <TranslationText
            text={getLanguageTranslation(translations, lang)}
            className={className}
            style={style}
          />
        ) : (
          <TranslationInput
            key={lang}
            lang={lang}
            translations={translations}
            onEditTranslation={onEditTranslation}
            className={className}
            style={style}
            label={label}
            placeholder={placeholder}
            required={required}
            multiline={multiline}
            rows={rows}
            autoFocus={autoFocus}
            size={size}
            onKeyDown={onKeyDown}
            disabled={disabled}
          />
        )}
      </div>
      {isModalOpen && (
        <TranslatedValueModal
          translations={translations}
          languages={languages}
          activeLang={lang}
          onSelectLang={setLang}
          onClose={() => setIsModalOpen(false)}
          onEditTranslation={onEditTranslation}
          optimizeTitle={optimizeTitle}
          className={className}
          style={style}
          label={label}
          placeholder={placeholder}
          maxLength={maxLength}
          multiline={multiline}
          rows={rows}
          size={size}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default TranslatedValue;
